import { AppConfig } from "./AppConfig.js";
import { ApiCache } from "./ApiCache.js";

// Import dinamico a proposito. Estatico, MockService arrastraba los JSON de data/ al bundle de
// produccion incluso con VITE_USE_MOCK=false — datos de muestra (documento, patente, telefono)
// viajando al navegador sin motivo. Ademas permite importar ApiService fuera de Vite, en los tests.
let mockPromise = null;
const cargarMock = () => {
  mockPromise ??= import("../services/MockService.js").then((m) => m.MockService);
  return mockPromise;
};

export class ApiService {
  static baseUrl = AppConfig.api.isLocal ? AppConfig.api.localUrl : AppConfig.api.baseUrl;
  static retryAttempts = AppConfig.api.retryAttempts;

  // Se registra desde main.js. Acá no se puede importar AuthService: AuthService ya importa
  // ApiService y seria un ciclo.
  static onSesionExpirada = null;
  static sesionExpiradaAvisada = false;

  /** Un 401 puede llegar por varios requests en vuelo a la vez: se avisa una sola vez. */
  static notificarSesionExpirada() {
    if (this.sesionExpiradaAvisada) return;
    this.sesionExpiradaAvisada = true;
    try {
      this.onSesionExpirada?.();
    } catch (e) {
      console.error("Error manejando la sesion expirada:", e);
    }
  }

  /** Tras un login nuevo el aviso vuelve a estar disponible. */
  static resetSesion() {
    this.sesionExpiradaAvisada = false;
  }

  static async get(endpoint, token = null, options = {}) {
    return this.makeRequest("GET", endpoint, null, token, options);
  }

  static async post(endpoint, data, token = null, options = {}) {
    return this.makeRequest("POST", endpoint, data, token, options);
  }

  static async put(endpoint, data, token = null, options = {}) {
    return this.makeRequest("PUT", endpoint, data, token, options);
  }

  static async patch(endpoint, data = null, token = null, options = {}) {
    return this.makeRequest("PATCH", endpoint, data, token, options);
  }

  static async postFormData(endpoint, formData, token = null) {
    return this.makeRequest("POST", endpoint, formData, token);
  }

  // Descargas binarias (PDF, CSV) — sin cache, devuelve Blob
  static async postBlob(endpoint, data, token = null) {
    const headers = {};
    if (token) this.applyAuthHeader(headers, token);
    if (data) headers["Content-Type"] = "application/json";
    const response = await fetch(this.baseUrl + "/" + endpoint, {
      method: "POST",
      headers,
      body: data ? JSON.stringify(data) : null,
    });
    if (!response.ok) throw new Error("HTTP error! status: " + response.status);
    return response.blob();
  }

  // Tras un POST/PUT que muta datos: ApiService.invalidate("polizas")
  static invalidate(endpointContains = "", token = null) {
    ApiCache.invalidate({ urlContains: this.baseUrl + "/" + endpointContains, token });
  }

  // AUTH_HEADER — FenixSeg usa 'x-token' con el JWT (ver data/README.md).
  // Si el back de autogestión decidiera Bearer: headers["Authorization"] = "Bearer " + token;
  static applyAuthHeader(headers, token) {
    headers["x-token"] = token;
  }

  static async makeRequest(method, endpoint, data = null, token = null, options = {}) {
    const url = this.baseUrl + "/" + endpoint;
    const {
      cacheTiempo = false,
      cachePersistent = true,
      forceRefresh = false,
      dedupe = true,
    } = options;

    const cache = AppConfig.api.cacheEnabled ? cacheTiempo : false;
    const ttlMs = ApiCache.parseTTL(cache);
    const cacheKey = ApiCache.buildKey({ method, url, data, token });
    const canCache = ApiCache.canCache({ ttlMs, data });

    if (canCache && !forceRefresh) {
      const cached = ApiCache.read(cacheKey);
      if (cached !== null) return cached;
    }

    if (dedupe) {
      const inflight = ApiCache.getInFlight(cacheKey);
      if (inflight) return inflight;
    }

    const requestPromise = (async () => {
      // El desvio a mock va aca adentro, en el lugar del fetch, y no en get()/post(): asi el modo
      // mock atraviesa el mismo cache y la misma deduplicacion que la API real. Si cortara antes,
      // lo que se prueba con VITE_USE_MOCK=true no seria el comportamiento que va a produccion.
      if (AppConfig.api.useMock) {
        const MockService = await cargarMock();
        const json =
          method === "GET"
            ? await MockService.get(endpoint, token, options)
            : await MockService.post(endpoint, data, token, options);
        if (canCache) ApiCache.write(cacheKey, json, ttlMs, cachePersistent);
        return json;
      }

      let lastError;
      for (let attempt = 1; attempt <= this.retryAttempts; attempt++) {
        try {
          const headers = {};
          if (token) this.applyAuthHeader(headers, token);
          let bodyToSend = data;
          if (data && !(data instanceof FormData)) {
            headers["Content-Type"] = "application/json";
            bodyToSend = JSON.stringify(data);
          }
          const response = await fetch(url, { method, headers, body: bodyToSend || null });
          if (!response.ok) {
            const error = new Error("HTTP error! status: " + response.status);
            error.status = response.status;
            throw error;
          }
          const json = await response.json();
          if (canCache) ApiCache.write(cacheKey, json, ttlMs, cachePersistent);
          return json;
        } catch (error) {
          lastError = error;
          if (AppConfig.features?.debug) console.warn("Intento " + attempt + " fallo:", error);

          // El token vencio: reintentar da 401 de nuevo. Se corta la sesion y se sale.
          if (error.status === 401) {
            this.notificarSesionExpirada();
            break;
          }
          // El resto de los 4xx tampoco cambia de resultado al repetirlo (403 ajeno, 404 inexistente).
          // El retry es para fallos de red y 5xx.
          if (error.status >= 400 && error.status < 500) break;

          if (attempt === this.retryAttempts) break;
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        }
      }
      console.error("API Error:", lastError);
      throw lastError;
    })();

    if (dedupe) ApiCache.setInFlight(cacheKey, requestPromise);
    try {
      return await requestPromise;
    } finally {
      if (dedupe) ApiCache.clearInFlight(cacheKey);
    }
  }
}
